import { useTranslation } from 'next-i18next'
import { useState, useMemo, useCallback } from 'react'
import { v4 as uuid } from 'uuid'

import ActivityWrapper from '~/bootcamp/Activities/components/ActivityWrapper'
import { ActivityProps } from '~/bootcamp/Activities/types/activities.types'
import AlgebraActivityWrapper from '~/bootcamp/Activities/Algebra/Components/AlgebraActivityWrapper'
import Equals from '~/bootcamp/Activities/components/Equals'
import Input from '~/bootcamp/Activities/components/Input'
import { equationPartsToLatex } from '~/bootcamp/Activities/Algebra/Algebra.utils'
import { hocOnEnterPress } from '~/bootcamp/Activities/utils/activities.utils'
import { useEmitInstance } from '~/bootcamp/Activities/utils/instance.utils'

import useInsertValue from './useInsertValue'

/** Mirrors the equationQuestion JSX, but a variable slot becomes \square (after the × the JSX
 *  paints) — the on-screen task is a fill-in-the-blank, not the symbolic form. */
function equationPartsToSquareExpression(parts: Array<{ integer: number; variable?: string }>): string {
  return parts
    .map(({ integer, variable }, i) => {
      const sign = i > 0 || integer < 0 ? (integer < 0 ? ' - ' : ' + ') : ''
      const coefficient = !variable || Math.abs(integer) !== 1 ? `${Math.abs(integer)}` : ''
      if (!variable) return `${sign}${coefficient}`
      const separator = Math.abs(integer) > 1 ? ' \\cdot ' : ''
      return `${sign}${coefficient}${separator}\\square`
    })
    .join('')
}

function InsertValue({
  answerState,
  actionButtonsFC,
  difficultyLevel,
  onAnswerStateChange,
  onInstanceChange,
  onNextQuestionClick,
  autofocus = true,
}: ActivityProps): React.ReactElement {
  const { t } = useTranslation('generics')
  const {
    equationParts,
    variableMap,
    answer,
    formValid,
    inputRef,
    onInputChange,
    handleCheckAnswer,
    goToNextQuestion,
  } = useInsertValue({
    difficultyLevel,
    onAnswerStateChange,
    onNextQuestionClick,
    answerState,
    autofocus,
  })

  useEmitInstance(
    onInstanceChange,
    equationParts.length > 0
      ? `${Array.from(variableMap.entries())
          .map(([mapVariable, value]) => `${mapVariable} = ${value}`)
          .join(', ')}. ${equationPartsToLatex(
          equationParts.map((part) => ({ ...part, exponent: part.variable ? 1 : undefined })),
        )} = ${equationPartsToSquareExpression(equationParts)}`
      : null,
  )

  const [inputKey, setInputKey] = useState<string>(uuid())

  const handleGoToNextQuestion = useCallback((): void => {
    goToNextQuestion()
    setInputKey(uuid())
  }, [goToNextQuestion, setInputKey])

  const equationInstructions = useMemo(() => {
    return (
      equationParts &&
      equationParts.map(({ integer, variable }, i) => (
        <span className="leading-10" key={i.toString()}>
          {(i > 0 || integer < 0) && ` ${integer < 0 ? '-' : '+'} `}
          {!variable || Math.abs(integer) !== 1 ? Math.abs(integer) : ''}
          <i>{variable}</i>
        </span>
      ))
    )
  }, [equationParts])

  const equationQuestion = useMemo(() => {
    const firstVariable = equationParts?.find((part) => !!part.variable)?.variable
    const onEnterPress = (): void => {
      if (formValid) {
        hocOnEnterPress(answerState, handleCheckAnswer, handleGoToNextQuestion)
      }
    }

    return (
      equationParts &&
      equationParts.map(({ integer, variable }, i) => {
        return (
          <span className="leading-10" key={i.toString()}>
            {(i > 0 || integer < 0) && ` ${integer < 0 ? '-' : '+'} `}
            {!variable || Math.abs(integer) !== 1 ? Math.abs(integer) : ''}
            {variable && Math.abs(integer) > 1 ? ' × ' : ''}
            {variable && (
              <Input
                aria-label={t('ariaLabelButtons.insertValue')}
                autoFocus={firstVariable === variable}
                isError={answerState === false}
                key={`${inputKey}-${variable}`}
                maxDigits={2}
                onChange={(value) => onInputChange(variable, value)}
                onEnter={onEnterPress}
                ref={firstVariable === variable ? inputRef : null}
                type="number"
                value={answer.get(variable) ?? ''}
              />
            )}
          </span>
        )
      })
    )
  }, [
    equationParts,
    formValid,
    answerState,
    handleCheckAnswer,
    handleGoToNextQuestion,
    t,
    inputKey,
    inputRef,
    answer,
    onInputChange,
  ])

  return (
    <ActivityWrapper>
      <AlgebraActivityWrapper className="flex-col">
        <div className="flex flex-row space-x-10">
          {Array.from(variableMap.keys()).map((variable) => (
            <span key={variable}>
              <i>{variable}</i> <Equals /> {variableMap.get(variable)}
            </span>
          ))}
        </div>
        <div className="flex flex-row items-center mt-10">
          <span className="block sm:inline-block">
            {equationInstructions}
            <Equals />
            {equationQuestion}
          </span>
        </div>
      </AlgebraActivityWrapper>
      {actionButtonsFC &&
        actionButtonsFC({
          formValid,
          correctAnswer: answerState ?? false,
          submitAnswerAction: handleCheckAnswer,
          clearFormAction: () => onInputChange('clear', ''),
          goToNextQuestionAction: handleGoToNextQuestion,
        })}
    </ActivityWrapper>
  )
}

export default InsertValue
